import { Braces, CalendarClock, Globe, Network, Plug, ShieldCheck } from 'lucide-react';
import { DnsArt, SslArt } from '@/modules/marketing/FeatureArt';
import {
  ApiMonitoringArt,
  CertCountdownArt,
  DomainExpiryArt,
  LookupArt,
  PortArt,
  PortMonitoringArt,
  RegistrationArt,
  ResponseArt,
  UptimeArt,
  WebsiteMonitoringArt,
} from '@/modules/marketing/MonitorArt';
import type { Article } from '@/modules/marketing/article-types';

/**
 * The six monitoring articles.
 *
 * Every one of these describes a monitor kind that exists in `MonitorKind`. Three of them — SSL,
 * DNS and domain expiry — were written only after the monitors were built, because an article
 * describing an unbuilt feature is a false claim in longer form.
 *
 * Alerts are described only as far as they go: email and webhook, to the targets a customer adds.
 * Auto-Fix has its own article, because it is a different promise from detection.
 *
 * No example domains or addresses in the prose: a reader should see what the check reports, not a
 * made-up host they might mistake for something real.
 */

export const websiteMonitoring: Article = {
  slug: 'website-monitoring',
  category: 'monitoring',
  icon: Globe,
  art: WebsiteMonitoringArt,
  eyebrow: 'Monitoring',
  title: 'Website Monitoring',
  seoTitle: 'Website Monitoring with Content Checks',
  metaDescription:
    'Monitor websites from outside your network: status code, response time and required page content. Every outage is recorded as an incident with details.',
  standfirst:
    'Website checks run from outside your infrastructure. Each check requests the page and verifies the status code, the response time and, optionally, text that must appear on the page.',
  sections: [
    {
      heading: 'Why a 200 status is not enough',
      art: ResponseArt,
      paragraphs: [
        'A site can return HTTP 200 while showing a database error, a blank page or an empty template. A check that only looks at the status code reports all of these as up.',
        'Adding a short phrase that only appears on a working page, such as a product name or a heading, confirms that the application works, not just the web server.',
      ],
    },
    {
      heading: 'Checks from outside your network',
      paragraphs: [
        'An agent on your server cannot report that the server itself is down or unreachable, and that is the outage that matters most.',
        'Checks run from the platform over the public internet, the same way visitors reach you. They test the whole path: DNS, network, web server and application.',
      ],
    },
    {
      heading: 'Incidents for every outage',
      art: UptimeArt,
      paragraphs: [
        'When a check fails, the platform opens an incident that records when the outage started, what the check saw and when the site recovered.',
        'You get a precise record, such as a six-minute outage with a 502 response and its start time, instead of knowing only that the site was down at some point.',
      ],
    },
    {
      heading: 'Choosing a page to monitor',
      paragraphs: [
        'The home page is often heavily cached, so it can load normally while the rest of the site is broken.',
        'A page that queries the database is a better choice. A dedicated health endpoint that checks its own dependencies and returns an error when they fail is better still.',
      ],
    },
    {
      heading: 'Best practices',
      paragraphs: [
        '**Choose a phrase that disappears when the site breaks.** Navigation text such as "Home" also appears on error pages. Content loaded from the database does not.',
        '**Do not monitor pages behind a login.** The check has no session, so it will always fail.',
        '**Watch response times, not only failures.** A page that slows from 200 ms to 4 seconds has not failed yet, but it needs attention.',
      ],
    },
  ],
  faq: [
    {
      q: 'What does a website check look at?',
      a: 'The HTTP status code against an expected range, the response time and, optionally, a phrase that must appear in the response body.',
    },
    {
      q: 'Where do checks run from?',
      a: 'From the platform, over the public internet. An agent on your server could not report an outage that takes the server offline.',
    },
    {
      q: 'How often does it check?',
      a: 'You set the interval per monitor, from every minute upwards. The timeout is kept well below the interval, so a slow response never overlaps the next check.',
    },
    {
      q: 'Will a slow site be reported as down?',
      a: 'No. A page that answers within 45 seconds is up, and an answer slower than 15 seconds is marked slow on the Monitoring page. A down alert needs four failed checks in a row, ten seconds apart.',
    },
    {
      q: 'Can I monitor a site on a server I have not connected?',
      a: 'Yes. A website or API monitor only needs a public address. Connecting the server as well lets Auto-Fix attempt a repair when a check fails.',
    },
    {
      q: 'Does it follow redirects?',
      a: 'No. A redirect is reported as the response, so a site that starts redirecting somewhere unexpected is not hidden.',
    },
  ],
  points: [
    'Status code, response time and page content',
    'Checks run from outside your network',
    'Outages recorded as incidents',
    'Any public URL, whether or not its server is connected',
    'Redirects reported, not followed',
  ],
  related: [
    { label: 'API monitoring', to: '/monitoring/api-monitoring' },
    { label: 'Auto-Fix', to: '/features/auto-fix' },
  ],
};

export const apiMonitoring: Article = {
  slug: 'api-monitoring',
  category: 'monitoring',
  icon: Braces,
  art: ApiMonitoringArt,
  eyebrow: 'Monitoring',
  title: 'API Monitoring',
  seoTitle: 'API and Health Check Endpoint Monitoring',
  metaDescription:
    'Monitor API and health check endpoints from outside your network. Check status codes, response times and response content to catch failures early.',
  standfirst:
    'APIs usually fail without anyone noticing until an integration breaks. An API monitor requests an endpoint on a schedule and checks the status code, response time and response content.',
  sections: [
    {
      heading: 'Why APIs need monitoring',
      paragraphs: [
        'When a website breaks, people notice. When an API breaks, webhooks stop arriving, mobile apps show stale data and scheduled syncs write nothing, often for days before anyone reports it.',
        'An API has no visitors to report a problem, so a scheduled check is often the only way a failure is noticed quickly.',
      ],
    },
    {
      heading: 'Monitor a health endpoint',
      paragraphs: [
        'The most useful endpoint to monitor is one built for it: a health endpoint that checks the service’s dependencies, such as the database and the queue, and returns an error status when any of them is unavailable.',
        'One check then covers everything the service depends on, and a failure points to a cause rather than a symptom.',
      ],
    },
    {
      heading: 'Check the response body',
      paragraphs: [
        'Many APIs return HTTP 200 with an error in the body. Frameworks and gateways often do this by default, and a check that reads only the status code treats it as healthy.',
        'Requiring text from a healthy response, such as an "ok" status or a field that only appears on success, catches these failures without the check needing to understand your schema.',
      ],
    },
    {
      heading: 'Unauthenticated checks',
      paragraphs: [
        'Checks do not send credentials. Monitors cannot store API keys, so the platform never holds a credential it would replay every minute.',
        'Expose a health endpoint that needs no authentication and returns no sensitive data. If your API is fully authenticated, adding one is worthwhile.',
      ],
    },
    {
      heading: 'Health endpoint best practices',
      paragraphs: [
        '**Keep it lightweight.** It can be checked every minute, so it should answer from a few quick queries rather than a full report.',
        '**Return meaningful status codes.** Return 503 when a dependency is down. An endpoint that always returns 200 tells you nothing.',
        '**Keep sensitive data out.** The endpoint is public and unauthenticated.',
        '**Watch response times.** APIs usually slow down before they fail.',
      ],
    },
  ],
  faq: [
    {
      q: 'How is API monitoring different from website monitoring?',
      a: 'It uses the same HTTP check. The difference is the target: an endpoint and an expected response instead of a page and a phrase.',
    },
    {
      q: 'Can it send authenticated requests?',
      a: 'No. Monitors cannot store API keys. Point the check at an unauthenticated health endpoint instead.',
    },
    {
      q: 'Can it check POST endpoints?',
      a: 'No. Checks are GET requests, so a monitor never creates data in your API.',
    },
    {
      q: 'Can it validate the JSON structure?',
      a: 'It checks that expected text appears in the response, which covers a status field or a known key without needing your schema.',
    },
    {
      q: 'What if the API is slow but still responding?',
      a: 'It is still up. Response time is recorded on every check, and an answer slower than 15 seconds is marked slow on the Monitoring page. Only failed checks lead to a down alert.',
    },
  ],
  points: [
    'Status code, response time and response content',
    'Designed for health check endpoints',
    'Catches 200 responses that contain errors',
    'No API keys stored',
    'Response time recorded on every check',
  ],
  related: [
    { label: 'Website monitoring', to: '/monitoring/website-monitoring' },
    { label: 'Port monitoring', to: '/monitoring/port-monitoring' },
  ],
};

export const sslMonitoring: Article = {
  slug: 'ssl-monitoring',
  category: 'monitoring',
  icon: ShieldCheck,
  art: SslArt,
  eyebrow: 'Monitoring',
  title: 'SSL Certificate Monitoring',
  seoTitle: 'SSL Certificate Expiry Monitoring',
  metaDescription:
    'Monitor TLS certificates on any host and port, including mail and database servers, and get alerted while there is still time to renew before expiry.',
  standfirst:
    'The SSL monitor reads the certificate on any host and port, and alerts you when the days remaining fall below your threshold, well before visitors see a browser warning.',
  sections: [
    {
      heading: 'Why certificate expiry matters',
      art: CertCountdownArt,
      paragraphs: [
        'An expired certificate replaces your site with a full-page browser warning that the connection is not private. Most visitors leave, and many assume the site has been compromised.',
        'Expiry is usually an administrative oversight, but the impact is immediate and reaches every visitor at once.',
      ],
    },
    {
      heading: 'Alerts before expiry',
      paragraphs: [
        'An uptime check only reports a problem after the certificate has expired. The SSL monitor fails while the certificate is still valid, as soon as the days remaining drop below your threshold.',
        'A threshold of 30 days is a good default. It turns renewal into routine work rather than an emergency.',
      ],
    },
    {
      heading: 'Certificates beyond the website',
      paragraphs: [
        'The certificates that expire unnoticed are often not on the website. They are on mail servers on ports 993 and 587, or on databases with TLS enabled on port 5432.',
        'Browsers never warn about these, so the first sign is clients failing to connect. You can monitor any host and port, not only 443.',
      ],
    },
    {
      heading: 'Verify automatic renewals',
      paragraphs: [
        'Automatic renewal can fail without warning: a renewal timer stops, a validation challenge starts failing after a firewall change, or a renewed certificate is written to disk while the server keeps serving the old one.',
        'The SSL monitor checks the certificate actually being served, whoever issued it, including certificates on third-party services, older servers and internal certificate authorities.',
        'Where Certbot was set up with the platform’s own playbooks, the Check Certificate Renewal playbook also runs a renewal dry run against Let’s Encrypt’s staging environment and confirms the renewal timer and the nginx reload hook. The monitor remains the check on what visitors are actually served.',
      ],
    },
    {
      heading: 'What the check verifies',
      paragraphs: [
        'The check reads the certificate the host presents and looks at its expiry date. It does not fail because a certificate is self-signed or issued for a different name, so it can still report on those certificates.',
        'Nothing is sent over the connection and no response is read. The certificate is inspected, not trusted.',
      ],
    },
    {
      heading: 'Choosing a threshold',
      paragraphs: [
        '**Match the threshold to your renewal process.** If renewing means raising a ticket with another team, 30 days may not be enough.',
        '**Monitor mail and database certificates too.** They are the ones most often forgotten.',
        '**Check daily.** The days remaining change once a day, so more frequent checks add nothing.',
      ],
    },
  ],
  faq: [
    {
      q: 'How far in advance can it warn?',
      a: 'Between 1 and 180 days. A longer window would leave normally renewed certificates permanently in a warning state.',
    },
    {
      q: 'Can it check certificates on ports other than 443?',
      a: 'Yes. Enter the host and the port, for example your mail server on port 993. Mail and database certificates are often the ones that expire.',
    },
    {
      q: 'My certificates renew automatically. Do I still need this?',
      a: 'Yes. It checks the result, not the renewal process. When the monitor is attached to a server, Auto-Fix reloads the web server if a renewed certificate is on disk but not yet being served.',
    },
    {
      q: 'Does it fail on self-signed certificates?',
      a: 'No. It reports on expiry. Refusing self-signed certificates would stop it reading them at all.',
    },
    {
      q: 'What happens if the certificate has already expired?',
      a: 'The check fails and reports the expiry date and how long ago it passed.',
    },
  ],
  points: [
    'Alerts while there is still time to renew',
    'Any host and port: web, mail or database',
    'Threshold of 1 to 180 days per monitor',
    'Reports on expired and self-signed certificates',
    'Checked daily',
  ],
  related: [
    { label: 'Auto-Fix', to: '/features/auto-fix' },
    { label: 'Server templates', to: '/features/server-templates' },
    { label: 'Domain expiry monitoring', to: '/monitoring/domain-expiry-monitoring' },
  ],
};

export const dnsMonitoring: Article = {
  slug: 'dns-monitoring',
  category: 'monitoring',
  icon: Network,
  art: DnsArt,
  eyebrow: 'Monitoring',
  title: 'DNS Monitoring',
  seoTitle: 'DNS Record Monitoring for A, MX and TXT',
  metaDescription:
    'Monitor DNS records and get alerted when a record disappears or changes. Supports A, AAAA, CNAME, MX, TXT and NS records at any DNS provider.',
  standfirst:
    'The DNS monitor looks up a record on a schedule and checks that the answer still contains the expected value. When a record changes or disappears, it tells you which one.',
  sections: [
    {
      heading: 'How DNS changes cause outages',
      art: LookupArt,
      paragraphs: [
        'Records get edited during unrelated work, zones are recreated from old exports, registrar parking records reappear, and records believed to be unused are deleted.',
        'In each case the server, the application and the certificate are fine, but the site is unreachable or points to the wrong address.',
      ],
    },
    {
      heading: 'Know which record changed',
      paragraphs: [
        'A website monitor will eventually notice the outage, but it looks like a failed server. It cannot tell you that a record now points somewhere else.',
        'The DNS monitor reports the value it found and the value it expected, so you fix the record instead of investigating a healthy server.',
      ],
    },
    {
      heading: 'Monitoring mail records',
      paragraphs: [
        'A missing MX record causes no visible outage: mail bounces or is delivered elsewhere. SPF, DKIM and DMARC records fail the same way. Remove one and mail keeps flowing while failing authentication, until major providers start rejecting it.',
        'You can monitor both MX and TXT records.',
      ],
    },
    {
      heading: 'Flexible matching',
      paragraphs: [
        'DNS answers rarely match exactly what you typed. MX answers include a priority, TXT records can come back in several parts, and CNAME and NS answers end with a trailing dot.',
        'To avoid false alarms, the expected value is matched as a case-insensitive substring, and trailing dots are ignored.',
      ],
    },
    {
      heading: 'Which records to monitor',
      paragraphs: [
        '**Start with the records outages come from.** The apex A record, www and your MX records cover most problems.',
        '**Match a distinctive part of the value.** For an SPF record, match your mail provider’s include rather than the full record, which changes whenever you add a sender.',
        '**Check hourly.** DNS changes are made by people, so checking every minute adds little.',
        '**Monitor every DNS provider you use.** The check is a standard lookup, so it works wherever a domain is hosted.',
      ],
    },
  ],
  faq: [
    {
      q: 'Which record types are supported?',
      a: 'A, AAAA, CNAME, MX, TXT and NS. SOA is not supported because its serial changes with every zone edit.',
    },
    {
      q: 'Does the value have to match exactly?',
      a: 'No. It is matched as a case-insensitive substring, and trailing dots are ignored, so MX priorities and multi-part TXT records do not cause false alarms.',
    },
    {
      q: 'Does the domain need to be connected to the platform?',
      a: 'No. The check is a standard DNS lookup, so it works for any domain at any DNS provider.',
    },
    {
      q: 'What happens if the record no longer exists?',
      a: 'The check fails and reports that no record of that type exists, which is a different message from a record with the wrong value.',
    },
    {
      q: 'Does it query every nameserver?',
      a: 'No. It resolves the record the way a client would. Comparing individual nameservers needs a different tool.',
    },
  ],
  points: [
    'A, AAAA, CNAME, MX, TXT and NS records',
    'Reports which record changed',
    'Substring matching avoids false alarms',
    'Works with any DNS provider',
    'Missing and wrong records reported separately',
  ],
  related: [
    { label: 'Domain expiry monitoring', to: '/monitoring/domain-expiry-monitoring' },
    { label: 'Website monitoring', to: '/monitoring/website-monitoring' },
  ],
};

export const domainExpiryMonitoring: Article = {
  slug: 'domain-expiry-monitoring',
  category: 'monitoring',
  icon: CalendarClock,
  art: DomainExpiryArt,
  eyebrow: 'Monitoring',
  title: 'Domain Expiry Monitoring',
  seoTitle: 'Domain Expiry Monitoring with RDAP',
  metaDescription:
    'Monitor domain registration expiry dates using RDAP data from the registry, and get alerted well before a lapsed domain takes your site and email offline.',
  standfirst:
    'The domain expiry monitor checks your registration’s expiry date directly with the registry. It covers a failure that no check on your own servers can detect.',
  sections: [
    {
      heading: 'What happens when a domain expires',
      art: RegistrationArt,
      paragraphs: [
        'When a registration lapses, your servers keep running and your certificates stay valid, but the domain stops resolving. Your website, email and APIs go offline together.',
        'The cause is rarely technical. Renewal notices go to an address nobody reads, the card on file expires, or the person who registered the domain has left.',
      ],
    },
    {
      heading: 'Grace periods and redemption',
      paragraphs: [
        'Most registries hold an expired domain for a short grace period, during which the owner can still renew, usually at a penalty. After that the domain enters redemption, which costs more, and is then released for anyone to register.',
        'Domains with traffic are often registered by someone else soon after release, so noticing early matters.',
      ],
    },
    {
      heading: 'Expiry dates from the registry',
      paragraphs: [
        'The expiry date is read from the registry over RDAP, the structured replacement for WHOIS, which returns a defined expiry field instead of free text formatted differently by every registry.',
        'Lookups go through the IANA bootstrap service, which sends each query to the registry responsible for the domain’s TLD.',
      ],
    },
    {
      heading: 'Registries without RDAP',
      paragraphs: [
        'Generic TLDs are required to run RDAP. Some country-code registries do not, and some that do withhold the expiry date.',
        'If the registry does not provide an expiry date, the monitor fails and says so, rather than reporting a check it could not perform as healthy.',
      ],
    },
    {
      heading: 'Best practices',
      paragraphs: [
        '**Set a threshold of at least 60 days.** Renewal is quick; regaining access to an old registrar account is not.',
        '**Monitor domains with auto-renew on.** Auto-renew fails silently when a payment card expires.',
        '**Use a shared contact address.** Registrar notices should go to a mailbox more than one person reads.',
        '**Check daily.** An expiry date changes rarely.',
      ],
    },
  ],
  faq: [
    {
      q: 'Where does the expiry date come from?',
      a: 'From the registry, over RDAP, using the IANA bootstrap service to find the registry responsible for the TLD.',
    },
    {
      q: 'Does it work with every domain extension?',
      a: 'Generic TLDs are required to support RDAP, so they work. Some country-code registries do not support it or withhold the date, and then the monitor reports that it could not get an answer.',
    },
    {
      q: 'Can I monitor a subdomain?',
      a: 'No. Only a registered domain has an expiry date, so subdomains are rejected rather than silently trimmed.',
    },
    {
      q: 'Will it renew my domain?',
      a: 'No. Renewal stays with your registrar. The monitor tells you the expiry date is approaching.',
    },
    {
      q: 'What if the domain is not registered?',
      a: 'The check fails and reports that the registry gave no answer, with the most likely reason.',
    },
  ],
  points: [
    'Expiry date read from the registry over RDAP',
    'Detects a failure your servers cannot see',
    'Threshold from 1 to 180 days ahead',
    'No answer from the registry is reported as a failure',
    'Registered domains only, not subdomains',
  ],
  related: [
    { label: 'DNS monitoring', to: '/monitoring/dns-monitoring' },
    { label: 'SSL certificate monitoring', to: '/monitoring/ssl-monitoring' },
  ],
};

export const portMonitoring: Article = {
  slug: 'port-monitoring',
  category: 'monitoring',
  icon: Plug,
  art: PortMonitoringArt,
  eyebrow: 'Monitoring',
  title: 'Port Monitoring',
  seoTitle: 'TCP Port Monitoring for Linux Services',
  metaDescription:
    'Monitor TCP ports for mail servers, databases, caches and custom services. Confirm each service accepts connections, checked from outside your network.',
  standfirst:
    'The port monitor opens a TCP connection, confirms it is accepted and closes it. Use it for databases, mail servers and other services that do not respond to HTTP.',
  sections: [
    {
      heading: 'Ping is not a service check',
      art: PortArt,
      paragraphs: [
        'A server that answers ping shows that the network and the kernel are up. It does not show whether PostgreSQL is listening, whether the mail server came back after a restart, or whether a worker is bound to its port.',
        'Services fail independently of the server, so a reachable server can still have services that are down.',
      ],
    },
    {
      heading: 'What a port check confirms',
      paragraphs: [
        'A port check confirms that something is accepting connections on that port. For databases and mail servers, it is the most that can be verified without authenticating.',
        'It catches common failures: a service that did not start after a reboot, a process that crashed, or a firewall rule changed during other work.',
      ],
    },
    {
      heading: 'Ports to monitor',
      paragraphs: [
        '**Mail.** Ports 25, 465 and 587. Mail failures are easy to miss, because messages simply stop arriving.',
        '**Databases.** Port 5432 for PostgreSQL and 3306 for MySQL. If a database only accepts local connections, a check from outside correctly reports it as refused.',
        '**Caches and queues.** Port 6379 for Redis. A failed cache often slows an application down instead of stopping it, which makes it harder to notice.',
        '**Custom services.** WebSocket servers, game servers and internal APIs on non-standard ports.',
      ],
    },
    {
      heading: 'Why port 22 is not supported',
      paragraphs: [
        'SSH cannot be monitored with a port check. Every connected server already has a connection test that checks SSH with the platform’s credentials, which confirms authentication, not only that the port is open.',
        'A port check on port 22 would still pass for a server the platform can no longer sign in to, which would be misleading.',
      ],
    },
    {
      heading: 'Limits of port checks',
      paragraphs: [
        'An open port does not guarantee a working service. A database can accept connections and still reject queries because its disk is full, and a mail server can accept connections and reject every message.',
        'If a service speaks HTTP, use a website or API monitor, which can inspect the response. Port monitoring is for services where accepting a connection is all that can be checked from outside.',
      ],
    },
  ],
  faq: [
    {
      q: 'What does a port check do?',
      a: 'It opens a TCP connection to a host and port, confirms the connection was accepted, and closes it. Nothing is sent or read.',
    },
    {
      q: 'Why can I not monitor port 22?',
      a: 'It is excluded on purpose. Each server’s connection test already checks SSH with the platform’s credentials, which verifies authentication rather than an open port.',
    },
    {
      q: 'My database only allows local connections. Will this work?',
      a: 'A check from outside will report the port as refused, which is correct. Port monitoring is for services that should be reachable from where the check runs.',
    },
    {
      q: 'Does it confirm the service is working?',
      a: 'No. It confirms that something accepted the connection. For services that speak HTTP, use a website or API monitor instead.',
    },
    {
      q: 'Can I monitor a port on a server not connected to the platform?',
      a: 'Yes. Any reachable host and port can be monitored.',
    },
  ],
  points: [
    'Confirms a service accepts connections',
    'Mail, databases, caches and custom ports',
    'Connects and closes; nothing sent or read',
    'Port 22 excluded; SSH is tested separately',
    'Use HTTP checks where a service supports them',
  ],
  related: [
    { label: 'Website monitoring', to: '/monitoring/website-monitoring' },
    { label: 'Security hardening', to: '/features/security-hardening' },
    { label: 'Auto-Fix', to: '/features/auto-fix' },
  ],
};
